import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { automationsClient } from '#pplx_generated/typespec/rest/automations/client.js';
export async function setupSlackDirectMessage(teamId, channelId, createGroup) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/slack/direct-message-setup', 'setup-slack-direct-message', { body: { team_id: teamId, channel_id: channelId, create_group: createGroup } });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Unable to set up Slack delivery',
            cause: error,
            status: response.status,
        });
    }
    return data.channel_id;
}
export async function createAutomation({ collectionUuid, name, prompt, schedule, triggers, modelPreference, notificationsEnabled, effortMode, reasoningEffort, }) {
    const { data, error, response } = await automationsClient.POST('/rest/automations', 'create-automation', {
        ...(triggers?.some((trigger) => trigger.kind === 'connector' && trigger.provider === 'custom_webhook')
            ? { params: { query: { supports_webhook_bearer: true } } }
            : {}),
        body: {
            name,
            prompt,
            ...(effortMode ? { effort_mode: effortMode } : {}),
            ...(reasoningEffort ? { reasoning_effort: reasoningEffort } : {}),
            ...(collectionUuid ? { collection_uuid: collectionUuid } : {}),
            ...schedule,
            ...(triggers ? { triggers: [...triggers] } : {}),
            ...(modelPreference !== undefined ? { model_preference: modelPreference } : {}),
            ...(notificationsEnabled !== undefined
                ? { notifications_enabled: notificationsEnabled }
                : {}),
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to create automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
async function editConnectorTrigger({ automationId, connectorTrigger, }) {
    const { error, response } = await automationsClient.POST('/rest/automations/{automation_id}/triggers/{trigger_id}/edit', 'edit-automation-trigger', {
        params: {
            path: {
                automation_id: automationId,
                trigger_id: connectorTrigger.triggerId,
            },
        },
        body: {
            filters: Object.fromEntries(Object.entries(connectorTrigger.filters).map(([field, matchers]) => [
                field,
                Object.fromEntries(Object.entries(matchers).map(([matcher, values]) => [matcher, [...values]])),
            ])),
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to edit automation trigger',
            cause: error,
            status: response.status ?? 0,
        });
    }
}
async function removeAutomationTrigger({ automationId, triggerId, }) {
    const { error, response } = await automationsClient.POST('/rest/automations/{automation_id}/triggers/{trigger_id}/delete', 'remove-automation-trigger', {
        params: {
            path: { automation_id: automationId, trigger_id: triggerId },
        },
    });
    // a retried save can re-delete a trigger the first attempt already removed;
    // that is the desired end state, not a failure. The client types status as the
    // op's declared codes, so widen before comparing to the error status.
    const status = response.status ?? 0;
    if (error && status !== 400) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to remove automation trigger',
            cause: error,
            status,
        });
    }
}
async function addAutomationTrigger({ automationId, replacingTriggerId, trigger, }) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/{automation_id}/triggers', 'add-automation-trigger', {
        params: {
            path: { automation_id: automationId },
            ...(trigger.provider === 'custom_webhook'
                ? { query: { supports_webhook_bearer: true } }
                : {}),
        },
        body: {
            trigger,
            ...(replacingTriggerId ? { replacing_trigger_id: replacingTriggerId } : {}),
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to add automation trigger',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
async function postAutomationEdit(automationId, body) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/{automation_id}/edit', 'edit-automation', {
        params: {
            path: {
                automation_id: automationId,
            },
        },
        body,
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to edit automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
export async function editAutomation({ addedTrigger, automationId, collectionUuid, connectorTrigger, name, prompt, removedTriggerIds, replacingTriggerId, schedule, modelPreference, notificationsEnabled, admissionInstructions, }, { onWebhookCredentials } = {}) {
    // The automation fields save first, then trigger edits/adds, then deletes. There
    // is no combined endpoint, so a mid-sequence failure leaves earlier steps applied
    // — replacements must create the validated new binding before deleting the old
    // one, otherwise a stale connection or transient add failure destroys the user's
    // working trigger.
    //
    // A rule edit validates server-side against the live trigger set, so its slot in
    // the sequence depends on direction: setting a rule waits until after the trigger
    // mutations (written first, it would gate the old set, and a then-rejected trigger
    // change would strand the save half-applied with the rule silently active), while
    // clearing one rides the first request so a stored rule cannot veto the incoming
    // trigger set.
    const triggerSetChanges = addedTrigger !== undefined || (removedTriggerIds?.length ?? 0) > 0;
    const deferredAdmissionInstructions = admissionInstructions != null && triggerSetChanges ? admissionInstructions : undefined;
    const data = await postAutomationEdit(automationId, {
        ...(name !== undefined ? { name } : {}),
        ...(prompt !== undefined ? { prompt } : {}),
        ...(collectionUuid !== undefined ? { collection_uuid: collectionUuid } : {}),
        ...schedule,
        ...(modelPreference !== undefined ? { model_preference: modelPreference } : {}),
        ...(notificationsEnabled !== undefined ? { notifications_enabled: notificationsEnabled } : {}),
        ...(admissionInstructions !== undefined && deferredAdmissionInstructions === undefined
            ? { admission_instructions: admissionInstructions }
            : {}),
    });
    if (connectorTrigger) {
        await editConnectorTrigger({ automationId, connectorTrigger });
    }
    const triggerIdReplacedByAdd = addedTrigger ? replacingTriggerId : undefined;
    const addedTriggerResponse = addedTrigger
        ? await addAutomationTrigger({
            automationId,
            replacingTriggerId: triggerIdReplacedByAdd,
            trigger: addedTrigger,
        })
        : undefined;
    if (addedTriggerResponse?.webhook_credentials?.length) {
        onWebhookCredentials?.(addedTriggerResponse.webhook_credentials);
    }
    for (const triggerId of removedTriggerIds ?? []) {
        if (triggerId === triggerIdReplacedByAdd) {
            continue;
        }
        await removeAutomationTrigger({ automationId, triggerId });
    }
    let finalData = data;
    if (deferredAdmissionInstructions !== undefined) {
        finalData = await postAutomationEdit(automationId, {
            admission_instructions: deferredAdmissionInstructions,
        });
    }
    // The add-trigger response is the only place newly provisioned webhook
    // credentials are returned. Preserve it through this composite edit flow.
    return addedTriggerResponse?.webhook_credentials?.length ? addedTriggerResponse : finalData;
}
export async function pauseAutomation({ automationId, }) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/{automation_id}/pause', 'pause-automation', {
        params: {
            path: {
                automation_id: automationId,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to pause automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
export async function resumeAutomation({ automationId, }) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/{automation_id}/resume', 'resume-automation', {
        params: {
            path: {
                automation_id: automationId,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to resume automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
export async function runAutomation({ automationId, }) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/{automation_id}/run-now', 'run-automation-now', {
        params: {
            path: {
                automation_id: automationId,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to run automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
export async function pinAutomation({ automationId, }) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/{automation_id}/pin', 'pin-automation', {
        params: {
            path: {
                automation_id: automationId,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to pin automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
export async function unpinAutomation({ automationId, }) {
    const { data, error, response } = await automationsClient.DELETE('/rest/automations/{automation_id}/pin', 'unpin-automation', {
        params: {
            path: {
                automation_id: automationId,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to unpin automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
export async function deleteAutomation({ automationId, }) {
    const { data, error, response } = await automationsClient.POST('/rest/automations/{automation_id}/delete', 'delete-automation', {
        params: {
            path: {
                automation_id: automationId,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to delete automation',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
//# sourceMappingURL=automationActions.js.map